import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import GlossaryLayout from "@/components/GlossaryLayout";
import useSEO from "@/hooks/useSEO";

const CLASSES = [
  {
    id: "1",
    label: "Class 1 — Explicit company-owned",
    color: "text-emerald-700",
    bg: "bg-emerald-50 border-emerald-200",
    badge: "bg-emerald-100 text-emerald-700 border-emerald-200",
    definition:
      "Knowledge that was intentionally created by or for the organisation, with clear authorship attributed to a role or team.",
    examples: [
      "Internal policy documents written by a named team",
      "Playbooks authored and approved by a department head",
      "Standard operating procedures with version history",
      "KB articles written by the company's content team",
    ],
    reviewBurden:
      "Lowest. A single designated reviewer can approve a Class 1 artifact. The evidence record documents authorship and approval — no lineage tracing required.",
    risk: "Low. Ownership is clear, authorship is attributable, and the company has an explicit right to use the content in AI systems.",
  },
  {
    id: "2",
    label: "Class 2 — Derived operational",
    color: "text-amber-700",
    bg: "bg-amber-50 border-amber-200",
    badge: "bg-amber-100 text-amber-700 border-amber-200",
    definition:
      "Knowledge extracted or inferred from operational data — support tickets, call recordings, CRM entries — generated by employees in the course of their work.",
    examples: [
      "Escalation patterns derived from Zendesk tickets",
      "Resolution sequences extracted from call recordings",
      "Recurring decision logic inferred from CRM entries",
      "Common objection-handling patterns from sales transcripts",
    ],
    reviewBurden:
      "Moderate. Class 2 artifacts require an evidence chain: reviewers must be able to trace the extracted pattern back to specific source records. The review documents not just whether the artifact is correct, but whether the company has clear rights to the derived knowledge.",
    risk: "Medium. The underlying work is company-owned, but the pattern was inferred rather than explicitly authored. PII exposure, attribution issues, and scope ambiguity are the primary risks.",
  },
  {
    id: "3",
    label: "Class 3 — Implicit individual",
    color: "text-rose-700",
    bg: "bg-rose-50 border-rose-200",
    badge: "bg-rose-100 text-rose-700 border-rose-200",
    definition:
      "Knowledge inferred from the patterns, preferences, or behaviours of specific individuals — rather than from team-level or company-level operational data.",
    examples: [
      "Resolution approaches inferred from a single agent's ticket history",
      "Decision patterns attributed to a named individual's judgement",
      "Personalised communication styles extracted from individual emails",
    ],
    reviewBurden:
      "Highest — and typically out of scope. Class 3 artifacts raise named-person voice mimicry issues, potential GDPR obligations around automated profiling, and employment law considerations. Most AI governance frameworks exclude Class 3 from their scope entirely.",
    risk: "High. These artifacts risk encoding an individual's implicit judgements without their knowledge or consent, creating voice-mimicry, attribution, and legal exposure.",
  },
];

const FAQS = [
  {
    q: "Who decides what class an artifact gets?",
    a: "Classification is determined by the characteristics of the source — not by the content of the extracted knowledge itself. The source system, the level of attribution available, and whether the knowledge was explicitly authored or inferred determine the class. In a governed workflow, classification happens at intake, before review begins.",
  },
  {
    q: "Can an artifact be reclassified?",
    a: "Yes. If new evidence about a source's authorship or ownership emerges — or if the extraction methodology changes — an artifact can be reclassified. The reclassification is logged in the evidence record, and the review requirements update accordingly.",
  },
  {
    q: "What happens if a Class 2 artifact can't be traced to source records?",
    a: "It should be blocked from publication until the lineage can be established. An artifact without a traceable evidence chain cannot be confidently attributed to company-owned operational data — which means the organisation may not have the rights to use the derived knowledge in an AI system.",
  },
  {
    q: "Is contributor class the same as data classification?",
    a: "No. Data classification (e.g. public / internal / confidential / restricted) governs who can access data and how it can be stored. Contributor class governs a different question: on what basis was this knowledge created, and what level of scrutiny is required before it can shape AI behaviour. The two systems are complementary, not redundant.",
  },
  {
    q: "Why is Class 3 typically out of scope?",
    a: "Class 3 artifacts — derived from individual behaviour rather than company-level operations — create disproportionate governance complexity relative to their value. Encoding an individual's implicit reasoning into an AI system without their knowledge raises consent, attribution, and employment law questions that most organisations are not equipped to resolve at scale. Excluding Class 3 from scope is a deliberate risk boundary, not an oversight.",
  },
];

export default function ContributorClass() {
  useSEO({
    title: "What is Contributor Class in AI Governance? — ProofMark Glossary",
    description:
      "Contributor class is a taxonomy that classifies AI knowledge sources by authorship and ownership — Class 1 (explicit company-owned), Class 2 (derived operational), and Class 3 (implicit individual). Learn how class determines review requirements and AI risk.",
    path: "/glossary/contributor-class",
  });

  useEffect(() => {
    const scriptId = "proofmark-jsonld-defined-term";
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "DefinedTerm",
      name: "Contributor Class",
      description:
        "A classification taxonomy that categorises AI knowledge sources by the nature of their authorship and ownership — Class 1 (explicit company-owned), Class 2 (derived operational), and Class 3 (implicit individual) — used to determine the review requirements and governance burden for each knowledge artifact before AI deployment.",
      inDefinedTermSet: {
        "@type": "DefinedTermSet",
        name: "ProofMark AI Governance Glossary",
        url: "https://proofmark-frontend-production.up.railway.app/glossary/contributor-class",
      },
    });
    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, []);

  return (
    <GlossaryLayout>
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-xs text-muted-foreground mb-8">
        <Link to="/" className="hover:text-foreground transition-colors">ProofMark</Link>
        <span>/</span>
        <span>Glossary</span>
        <span>/</span>
        <span className="text-foreground">Contributor Class</span>
      </nav>

      {/* Hero */}
      <div className="mb-10">
        <span className="tracked-caps">AI Governance Glossary</span>
        <h1 className="font-serif-display text-4xl mt-2 mb-4">Contributor Class</h1>
        <p className="text-muted-foreground text-lg max-w-2xl leading-relaxed">
          A taxonomy for classifying AI knowledge sources by authorship and ownership — the single
          most important factor in determining how much scrutiny a knowledge artifact requires before
          it can be deployed in an AI system.
        </p>
      </div>

      {/* Definition box */}
      <div className="border-l-4 border-brand pl-6 py-4 bg-brand/5 rounded-r-lg mb-12">
        <p className="text-xs tracked-caps text-muted-foreground mb-2">Definition</p>
        <p className="text-base leading-relaxed">
          <strong>Contributor class</strong> is a classification system that categorises AI knowledge
          sources along a spectrum from explicit company ownership (Class 1) to inferred individual
          behaviour (Class 3). Class determines the review requirements, evidence chain obligations,
          and risk profile for every knowledge artifact extracted from that source.
        </p>
      </div>

      {/* Why classification comes first */}
      <section className="mb-12">
        <h2 className="font-serif-display text-2xl mb-4">Why classification comes before review</h2>
        <div className="max-w-3xl space-y-4 text-muted-foreground leading-relaxed">
          <p>
            Before any knowledge artifact enters a review workflow, the organisation needs to know
            what kind of knowledge it is dealing with. The same escalation rule looks very different
            depending on whether it was written by a policy team (Class 1), inferred from 4,000
            support tickets (Class 2), or extracted from one agent&apos;s ticket history (Class 3).
          </p>
          <p>
            Classification at intake sets the correct review gate and prevents two common failure
            modes: under-scrutinising Class 2 artifacts (treating derived patterns as if they had
            the same standing as authored policy) and over-scrutinising Class 1 artifacts (applying
            full lineage-tracing requirements to a document that has clear authorship already
            documented).
          </p>
          <p>
            Getting classification right also surfaces the right policy questions early. A Class 2
            artifact immediately raises questions about PII in source records. A Class 3 artifact
            immediately raises named-person phrasing and consent concerns. Catching these at
            classification — not during review — saves significant governance effort downstream.
          </p>
        </div>
      </section>

      {/* The three classes */}
      <section className="mb-12">
        <h2 className="font-serif-display text-2xl mb-6">The three contributor classes</h2>
        <div className="space-y-6 max-w-3xl">
          {CLASSES.map((cls) => (
            <div key={cls.id} className={`border rounded-lg p-6 ${cls.bg}`}>
              <div className="flex items-center gap-3 mb-4">
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${cls.badge}`}>
                  Class {cls.id}
                </span>
                <p className={`font-medium ${cls.color}`}>{cls.label}</p>
              </div>

              <p className="text-sm leading-relaxed mb-5">{cls.definition}</p>

              <div className="space-y-4">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                    Typical examples
                  </p>
                  <ul className="space-y-1">
                    {cls.examples.map((ex) => (
                      <li key={ex} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <span className="h-1.5 w-1.5 rounded-full bg-current mt-1.5 shrink-0 opacity-50" />
                        {ex}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
                    Review burden
                  </p>
                  <p className="text-sm text-muted-foreground leading-relaxed">{cls.reviewBurden}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
                    AI risk profile
                  </p>
                  <p className="text-sm text-muted-foreground leading-relaxed">{cls.risk}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* How class shapes AI behaviour */}
      <section className="mb-12">
        <h2 className="font-serif-display text-2xl mb-4">
          How contributor class affects AI behaviour risk
        </h2>
        <div className="max-w-3xl space-y-4 text-muted-foreground leading-relaxed">
          <p>
            The connection between contributor class and AI risk is direct. An AI system trained on
            or prompted with Class 1 artifacts inherits knowledge with clear ownership and minimal
            provenance uncertainty. The company knows who authored it, when, and for what purpose.
          </p>
          <p>
            A system using Class 2 artifacts carries more uncertainty. The patterns are real — they
            reflect how the company&apos;s teams actually resolve cases — but they were inferred, not
            explicitly authored. If the inference is wrong, or if the source data contained PII, or
            if the pattern reflects a biased resolution practice, those problems are encoded into AI
            behaviour without ever appearing in a policy document.
          </p>
          <p>
            Class 3 introduces the additional problem of voice and attribution. When an AI system
            is effectively replicating how a specific individual thinks and decides — without their
            knowledge or consent — the organisation faces legal and ethical exposure that extends
            well beyond data governance.
          </p>
        </div>
      </section>

      {/* Free tool CTA */}
      <div className="border border-brand/30 rounded-lg p-5 bg-brand/5 max-w-2xl mb-12">
        <p className="font-medium text-sm mb-2">Not sure what class your data source is?</p>
        <p className="text-sm text-muted-foreground mb-3">
          The Contributor Class Classifier takes a plain-language description of your source and
          returns a Class 1, 2, or 3 classification with reasoning and review requirements.
        </p>
        <Link
          to="/tools/contributor-class-classifier"
          className="inline-flex items-center gap-1.5 rounded-md bg-brand text-brand-foreground px-4 py-2 text-sm font-medium hover:opacity-90 transition-opacity"
        >
          Try the free Classifier →
        </Link>
      </div>

      {/* Related terms */}
      <section className="mb-12">
        <h2 className="font-serif-display text-2xl mb-4">Related terms</h2>
        <div className="grid sm:grid-cols-2 gap-3 max-w-2xl">
          <Link
            to="/glossary/ai-knowledge-artifact"
            className="border border-border rounded-lg p-4 bg-card hover:border-brand/40 transition-colors group"
          >
            <p className="font-medium text-sm group-hover:text-brand transition-colors">AI Knowledge Artifact →</p>
            <p className="text-xs text-muted-foreground mt-1">
              The discrete unit that contributor class is applied to at intake.
            </p>
          </Link>
          <Link
            to="/glossary/ai-knowledge-governance"
            className="border border-border rounded-lg p-4 bg-card hover:border-brand/40 transition-colors group"
          >
            <p className="font-medium text-sm group-hover:text-brand transition-colors">AI Knowledge Governance →</p>
            <p className="text-xs text-muted-foreground mt-1">
              The broader practice contributor class sits within.
            </p>
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="mb-12">
        <h2 className="font-serif-display text-2xl mb-6">Frequently asked questions</h2>
        <div className="space-y-5 max-w-3xl">
          {FAQS.map((faq) => (
            <div key={faq.q} className="border border-border rounded-lg p-5 bg-card">
              <p className="font-medium text-sm mb-2">{faq.q}</p>
              <p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <div className="border border-brand/30 rounded-lg p-6 bg-brand/5 max-w-2xl">
        <p className="font-medium mb-2">Apply contributor class at scale</p>
        <p className="text-sm text-muted-foreground mb-4">
          ProofMark classifies every knowledge source at intake and gates the review workflow by
          class — ensuring Class 2 artifacts are never treated as Class 1, and Class 3 artifacts
          never reach your AI systems.
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 rounded-md bg-brand text-brand-foreground px-4 py-2 text-sm font-medium hover:opacity-90 transition-opacity"
        >
          Request early access
        </Link>
      </div>
    </GlossaryLayout>
  );
}
